/**
 * The card two boards use to point at each other instead of both drawing the
 * same number.
 *
 * `/dynamics` owns the F&O **server** side: availability, forms, requests,
 * queues, and the slow interactions with their classification and the Cato
 * network half. `/experience` owns **browser** experience across the five apps
 * the managed extension reports on — TTFB, interaction latency, errors, idle
 * probes, and the coverage ratio that says how much of the fleet is reporting
 * at all. F&O appears on both boards, and that is correct: it is one of the five
 * apps and it is also the ERP. What is not correct is printing the same figure
 * twice, because then two boards can disagree and a reader has no way to tell
 * which is stale.
 *
 * So each shared number has exactly one owner, and the other board carries this
 * card: the owner named in one line, and a door. It is deliberately not a
 * `Statistic` — a cross-link that showed a value would be the duplication it
 * exists to remove.
 */

import { ArrowRight, Icon, type LucideIcon } from "@norican/icons";
import { Card, Typography } from "@norican/react";
import { Link } from "react-router";

interface BoardCrossLinkProps {
  /** What lives on the other board, as a noun phrase. */
  title: string;
  /** One line: which board owns it, and why it is owned there. */
  caption: string;
  /** Where to go — a path, optionally with an anchor or drill params. */
  to: string;
  /** The link text, e.g. "Application experience →". */
  cta: string;
  icon: LucideIcon;
}

export function BoardCrossLink({ title, caption, to, cta, icon }: BoardCrossLinkProps) {
  return (
    <Card variant="subtle" className="h-full">
      <Card.Body>
        <div className="flex h-full flex-col gap-2">
          <div className="flex items-center gap-2">
            <Icon icon={icon} size="sm" aria-hidden="true" />
            <Typography variant="heading-4" as="h2">
              {title}
            </Typography>
          </div>
          <Typography variant="body-sm" tone="muted" className="flex-1">
            {caption}
          </Typography>
          {/* `text-accent`, not `text-accent-fg`: the latter is the foreground
              painted ON an accent surface (white in the light theme) and
              disappears against the card. */}
          <Link
            to={to}
            className="text-accent inline-flex items-center gap-1 rounded text-sm font-medium hover:underline focus-visible:outline-2"
          >
            {cta}
            <Icon icon={ArrowRight} size="xs" aria-hidden="true" />
          </Link>
        </div>
      </Card.Body>
    </Card>
  );
}
